/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * Wires the optional prev/next buttons to scroll the track by roughly one page and toggles
 * their `disabled` state at the start/end of the scroll range. The track is a native
 * scroll-snap container, so scrolling, swiping, and keyboard focus all work without JS.
 *
 * Optional autoplay: set `autoplay="<seconds>"` on the element. It advances one item at a
 * time, loops back to the start at the end, pauses while hovered or focused, never runs
 * when the visitor prefers reduced motion, and a `[data-carousel-toggle]` button lets the
 * visitor pause and resume it (its aria-pressed reflects the paused state).
 *
 * Expected DOM:
 *   <s-carousel [autoplay="5"]>
 *     [<button data-carousel-prev> / <button data-carousel-next> / <button data-carousel-toggle>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");
    this.toggleButton = this.querySelector("[data-carousel-toggle]");

    if (!this.track) return;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.setupAutoplay();
    this.updateControls();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.toggleButton?.removeEventListener("click", this.onToggle);
    this.removeEventListener("pointerenter", this.onHold);
    this.removeEventListener("pointerleave", this.onRelease);
    this.removeEventListener("focusin", this.onHold);
    this.removeEventListener("focusout", this.onRelease);
    this.resizeObserver?.disconnect();
    clearInterval(this.timer);
  }

  setupAutoplay() {
    const seconds = parseFloat(this.getAttribute("autoplay"));
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!seconds || reduceMotion) {
      if (this.toggleButton) this.toggleButton.hidden = true;
      return;
    }

    this.paused = false;
    this.held = false;
    this.timer = setInterval(() => {
      if (!this.paused && !this.held) this.advance();
    }, seconds * 1000);

    this.onHold = () => (this.held = true);
    this.onRelease = (event) => {
      if (event.type === "focusout" && this.contains(event.relatedTarget)) return;
      this.held = false;
    };
    this.onToggle = () => {
      this.paused = !this.paused;
      this.toggleButton.setAttribute("aria-pressed", String(this.paused));
    };

    this.addEventListener("pointerenter", this.onHold);
    this.addEventListener("pointerleave", this.onRelease);
    this.addEventListener("focusin", this.onHold);
    this.addEventListener("focusout", this.onRelease);
    this.toggleButton?.addEventListener("click", this.onToggle);
  }

  /** Move forward one item, looping back to the start at the end. */
  advance() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 1) return;
    if (this.track.scrollLeft >= maxScroll - 1) {
      this.track.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    const item = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
    this.track.scrollBy({ left: step, behavior: "smooth" });
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
